import React, { useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { t, font, Panel, Input, EmptyState } from '@realtyflows/ui';
import { CATEGORIES, ARTICLE_BY_SLUG, articlesByCategory, searchArticles } from './registry';

/**
 * Help center.
 *
 * Two views, one component:
 *   /help          → category landing + search
 *   /help/:slug    → single article with sidebar nav
 *
 * Public route — also reachable when logged out so people can search docs
 * without signing in. Renders inside the public layout when there's no user.
 */
export default function Help() {
  const { slug } = useParams();
  const article = slug ? ARTICLE_BY_SLUG[slug] : null;
  const grouped = useMemo(() => articlesByCategory(), []);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const searchResults = query.trim() ? searchArticles(query) : null;

  return (
    <div style={{ background: t.pageBg, minHeight: '100vh', fontFamily: font.body }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '40px 24px' }}>
        <header style={{ marginBottom: 32 }}>
          <Link to="/help" style={{ textDecoration: 'none' }}>
            <h1 style={{
              fontFamily: font.display,
              color: t.textPrimary,
              fontSize: 36,
              margin: 0,
              fontWeight: 700,
            }}>
              Help Center
            </h1>
          </Link>
          <p style={{ color: t.textMuted, margin: '8px 0 24px', fontSize: 16 }}>
            Guides, tips, and answers for every part of RealtyFlows.
          </p>
          <div style={{ maxWidth: 520 }}>
            <Input
              type="search"
              placeholder="Search articles..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus={!article}
            />
          </div>
        </header>

        <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 32, alignItems: 'start' }}>
          <SideNav grouped={grouped} activeSlug={slug} />

          <div>
            {searchResults !== null ? (
              <SearchResults results={searchResults} query={query} onPick={(s) => { setQuery(''); navigate(`/help/${s}`); }} />
            ) : article ? (
              <ArticleView article={article} />
            ) : (
              <Landing grouped={grouped} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SideNav({ grouped, activeSlug }) {
  return (
    <nav style={{
      position: 'sticky', top: 24,
      maxHeight: 'calc(100vh - 48px)', overflowY: 'auto',
      paddingRight: 8,
    }}>
      {grouped.map((cat) => (
        <div key={cat.id} style={{ marginBottom: 20 }}>
          <div style={{
            fontFamily: font.display, fontSize: 11, fontWeight: 700,
            letterSpacing: 1.2, textTransform: 'uppercase',
            color: t.textMuted, marginBottom: 6,
          }}>
            {cat.label}
          </div>
          {cat.articles.map((a) => (
            <Link
              key={a.slug}
              to={`/help/${a.slug}`}
              style={{
                display: 'block',
                padding: '6px 10px',
                margin: '2px -10px',
                borderRadius: 6,
                fontSize: 14,
                color: a.slug === activeSlug ? t.textPrimary : t.textSecondary,
                background: a.slug === activeSlug ? t.surfaceWarm : 'transparent',
                textDecoration: 'none',
                fontWeight: a.slug === activeSlug ? 600 : 400,
              }}
            >
              {a.title}
            </Link>
          ))}
        </div>
      ))}
    </nav>
  );
}

function Landing({ grouped }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      {grouped.map((cat) => (
        <Panel key={cat.id} style={{ padding: 20 }}>
          <h2 style={{
            fontFamily: font.display, fontSize: 18, fontWeight: 700,
            color: t.textPrimary, margin: '0 0 12px',
          }}>
            {cat.label}
          </h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {cat.articles.map((a) => (
              <li key={a.slug} style={{ marginBottom: 6 }}>
                <Link
                  to={`/help/${a.slug}`}
                  style={{
                    color: t.sky, textDecoration: 'none',
                    fontSize: 14, fontWeight: 500,
                  }}
                >
                  {a.title}
                </Link>
                {a.summary && (
                  <div style={{ color: t.textMuted, fontSize: 13, marginTop: 2 }}>
                    {a.summary}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Panel>
      ))}
    </div>
  );
}

function SearchResults({ results, query, onPick }) {
  if (!results.length) {
    return (
      <EmptyState
        title="No matches"
        body={`Nothing matched "${query}". Try a broader term, or browse by category from the side nav.`}
      />
    );
  }
  return (
    <div>
      <div style={{ color: t.textMuted, fontSize: 13, marginBottom: 12 }}>
        {results.length} result{results.length === 1 ? '' : 's'} for "{query}"
      </div>
      {results.map((a) => (
        <Panel
          key={a.slug}
          style={{ padding: 16, marginBottom: 10, cursor: 'pointer' }}
          onClick={() => onPick(a.slug)}
        >
          <div style={{
            fontFamily: font.display, fontSize: 15, fontWeight: 600,
            color: t.textPrimary, marginBottom: 4,
          }}>
            {a.title}
          </div>
          <div style={{ color: t.textMuted, fontSize: 13 }}>{a.summary}</div>
        </Panel>
      ))}
    </div>
  );
}

function ArticleView({ article }) {
  const Body = article.Body;
  const cat = CATEGORIES.find((c) => c.id === article.category);
  const related = (article.related || [])
    .map((s) => ARTICLE_BY_SLUG[s])
    .filter(Boolean);

  return (
    <article style={{
      background: t.surface,
      borderRadius: 12,
      padding: '32px 40px',
      border: `1px solid ${t.border}`,
    }}>
      {cat && (
        <div style={{
          fontFamily: font.display, fontSize: 11, fontWeight: 700,
          letterSpacing: 1.2, textTransform: 'uppercase',
          color: t.emerald, marginBottom: 8,
        }}>
          {cat.label}
        </div>
      )}
      <h1 style={{
        fontFamily: font.display, fontSize: 28, fontWeight: 700,
        color: t.textPrimary, margin: '0 0 8px',
      }}>
        {article.title}
      </h1>
      {article.summary && (
        <p style={{ color: t.textMuted, fontSize: 16, margin: '0 0 24px', lineHeight: 1.5 }}>
          {article.summary}
        </p>
      )}

      <div style={{ color: t.textSecondary, fontSize: 15, lineHeight: 1.7 }} className="help-article-body">
        <Body />
      </div>

      {related.length > 0 && (
        <div style={{ marginTop: 40, paddingTop: 24, borderTop: `1px solid ${t.border}` }}>
          <div style={{
            fontFamily: font.display, fontSize: 11, fontWeight: 700,
            letterSpacing: 1.2, textTransform: 'uppercase',
            color: t.textMuted, marginBottom: 12,
          }}>
            Related
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/help/${r.slug}`}
                style={{ color: t.sky, textDecoration: 'none', fontSize: 14 }}
              >
                → {r.title}
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
